/**
 * What the four dashboards share.
 *
 * The tile strip is the same shape on all of them because the server builds it
 * the same way: a label, a value, the kind of figure it is, and the section
 * whose explainer answers for it. That last field is why every tile carries its
 * own information button rather than relying on the page's: the question a
 * person asks of a number is how that number was worked out, and the page
 * explainer answers a different question.
 */
import type { Dict } from '../../api/types'
import { useSession } from '../../app/session'
import { Tile, Tiles, toneOf } from '../../components/bits'
import { useExplain } from '../../components/Explainer'

export interface TileData {
  label: string
  value: unknown
  kind: string
  section: string | null
  tone?: string | null
  suffix?: string | null
}

export function TileStrip({ tiles }: { tiles: TileData[] }) {
  const { amount, n } = useSession()
  const explain = useExplain()

  const shown = (t: TileData) => {
    if (t.value === null || t.value === undefined) return '—'
    switch (t.kind) {
      case 'money': return amount(t.value, null, 0)
      case 'pct': return `${n(t.value, 1)}%`
      case 'score': return n(t.value, 1)
      case 'hours': return `${n(t.value, 1)} h`
      default: return typeof t.value === 'number' ? n(t.value) : String(t.value)
    }
  }

  return (
    <Tiles>
      {tiles.map((t) => (
        <Tile key={t.label} label={t.label} value={shown(t)}
              sub={t.suffix || undefined}
              tone={(t.tone as never) || undefined}
              onExplain={t.section ? () => explain(t.section!, t.label) : undefined} />
      ))}
    </Tiles>
  )
}

/** A small horizontal bar for a distribution, sized against the largest value.
 *  Against the largest rather than against a fixed scale, because a dashboard
 *  where every bar is three pixels wide communicates nothing. */
export function Bars({ rows, labelKey, valueKey, format }: {
  rows: Dict[]
  labelKey: string
  valueKey: string
  format?: (v: unknown) => string
}) {
  const { n, lbl } = useSession()
  const max = rows.reduce((m, r) => Math.max(m, Number(r[valueKey]) || 0), 0) || 1
  return (
    <div className="bars">
      {rows.map((r, i) => (
        <div className="bar-row" key={String(r[labelKey]) || i}>
          <span className="bar-label">{lbl(r[labelKey])}</span>
          <div className="bar-track">
            <i style={{ width: `${(Number(r[valueKey]) / max) * 100}%` }} />
          </div>
          <span className="bar-value num">
            {format ? format(r[valueKey]) : n(r[valueKey])}
          </span>
        </div>
      ))}
    </div>
  )
}

export function StateChips({ rows, labelKey, valueKey }: {
  rows: Dict[]; labelKey: string; valueKey: string
}) {
  const { n, lbl } = useSession()
  return (
    <div className="chips">
      {rows.map((r) => (
        <span className={`b ${toneOf(r[labelKey])}`} key={String(r[labelKey])}>
          {lbl(r[labelKey])} · {n(r[valueKey])}
        </span>
      ))}
    </div>
  )
}
